import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { ToastService } from './toast.service';
import { ToastComponent } from './toast.component';

/**
 * Container component that renders active toast notifications.
 * Place this component once in your application's root/shell component.
 *
 * @example
 * ```html
 * <!-- In your app.component.html or shell component -->
 * <zag-toast-container />
 * ```
 */
@Component({
  selector: 'zag-toast-container',
  imports: [ToastComponent],
  template: `
    @for (toast of visibleToasts(); track toast.id) {
      <zag-toast
        [id]="toast.id"
        [status]="toast.status"
        [title]="toast.title"
        [message]="toast.message"
        [dismissible]="toast.dismissible"
        [action]="toast.action"
        (dismissed)="onDismiss($event)"
      />
    }
  `,
  styleUrl: './toast-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-toast-container]': 'true',
    '[class.zag-toast-container--top]': 'position() === "top"',
    '[class.zag-toast-container--top-right]': 'position() === "top-right"',
    '[class.zag-toast-container--top-left]': 'position() === "top-left"',
    '[class.zag-toast-container--bottom]': 'position() === "bottom"',
    '[class.zag-toast-container--bottom-right]': 'position() === "bottom-right"',
    '[class.zag-toast-container--bottom-left]': 'position() === "bottom-left"',
    'aria-live': 'polite',
    'aria-label': 'Notifications',
  },
})
export class ToastContainerComponent {
  private readonly toastService = inject(ToastService);

  readonly visibleToasts = computed(() => this.toastService.toasts());
  readonly position = computed(() => this.toastService.position());

  onDismiss(id: string): void {
    this.toastService.dismiss(id);
  }
}
